import {Component, Inject, Vue} from "vue-property-decorator";
import {AppSettings} from "~/lib/app-settings";
import {Getter} from "vuex-class";
import {Notification} from "~/modules/notifications/lib/notification";
import {PublicationOptions} from "~/modules/notifications/lib/publication-options";
import {Recipients} from "~/modules/notifications/lib/recipients";
import {clientToDTO, PartnerClientDto} from "~/lib/models/app-settings";
import {publishNotification} from "~/modules/notifications/service";
import {RecipientsSelector} from "~/modules/notifications/components/recipients-selector";
import {isDefined} from "~/lib/helpers/guards";
import {getClientDetails} from "~/modules/client/service";
import {getPartnerClients} from "~/modules/partner/services";
import {sort} from "~/lib/helpers";
import {FeedNotificationEditor} from "~/modules/notifications/components/feed-notification-editor";
import {FlashNotificationEditor} from "~/modules/notifications/components/flash-notification-editor";
import {Columns} from "~/components/grid/columns";
import {Column} from "~/components/grid/column";
import XSelect from "~/components/forms/x-select";

const typeOptions = [
  {value: "feed", label: "Fil de notifications"},
  {value: "flash", label: "Flash"},
];

@Component
export class NotificationsEditor extends Vue {

  @Inject() appSettings: AppSettings;
  @Getter currentUser;

  protected notification: Notification = null;
  protected options: PublicationOptions = null;
  protected recipients: Recipients = null;
  protected clients: PartnerClientDto[] = [];

  get canPublish() {
    return this.recipients.isValid() && this.notification.isValid();
  }

  get notificationOwnerUUID() {
    return this.appSettings.clientUUID || this.currentUser.client;
  }

  async created() {
    await this.reconcileClients();
    await this.initData();
  }

  async publish() {
    if (this.canPublish) {
      try {
        await publishNotification(this.notification, this.options, this.recipients);
        this.$notify.success("Publication effectuée");
        await this.initData();
        (this.$refs.recipientsSelector as RecipientsSelector).reset();
      } catch (err) {
        this.$notify.error("Erreur lors de la publication");
      }
    }
  }

  async initData() {
    this.notification = new Notification(this.notificationOwnerUUID, isDefined(this.notification) ? this.notification.type : "feed");
    this.options = new PublicationOptions();
    this.recipients = new Recipients();
  }

  async reconcileClients() {

    let clients = [];

    // We are currently editing a client
    if (isDefined(this.appSettings.clientUUID)) {
      clients.push(clientToDTO(await getClientDetails(this.appSettings.clientUUID)));
    } else {
      try {
        clients = await getPartnerClients();
      } catch (err) {
        this.$notify.error(`Getting clients partner failed.`);
        console.error(err.message);
      }
    }

    if (clients.length === 0) {
      this.$notify.error("Impossible de charger la configuration client. Merci de contacter le support.");
    }

    this.clients = sort(clients, (a, b) => {
      if (a.name > b.name) return 1;
      if (a.name < b.name) return -1;
      return 0;
    });
  }

  changeNotificationType(v) {
    this.notification.type = v.value;
    this.options.flash = v.value == 'flash';
  }

  renderEditor() {
    return this.notification.type === "feed" ?
      <FeedNotificationEditor notification={this.notification} options={this.options}/> :
      <FlashNotificationEditor notification={this.notification}/>;
  }

  render() {
    if (!this.notification) return null;
    return (
      <div>
        <Columns>
          <Column>
            <h2 class="page-subtitle">Type de notification</h2>
            <XSelect
              value={this.notification.type}
              data={typeOptions}
              label={role => role.label}
              equality={(r, v) => r?.value === v}
              onInput={this.changeNotificationType}
            />
          </Column>
        </Columns>

        <h2 class="page-subtitle">Edition</h2>
        {this.renderEditor()}

        <RecipientsSelector
          ref="recipientsSelector"
          recipients={this.recipients}
          availableClients={this.clients}
        />

        <div class="actions m-t-20">
          <button class="button is-primary" disabled={!this.canPublish} onClick={this.publish}>Publier</button>
        </div>
      </div>
    );

  }
}

// VUE JSX HOT LOADER //
if (module.hot) require("/usr/src/app/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-9f6daba2/notifications-editor.tsx" });